import { Mutex } from 'async-mutex'
import { debounce } from 'lodash'
import PQueue from 'p-queue'

import { AppBuilderAPI } from '@makyoapp/makyo-sdk'
import { ILogoFileLink, IUpdateBrandKitBody } from '@makyoapp/makyo-sdk/api/BrandKits'
import { IUpdateProjectPrivacyBody } from '@makyoapp/makyo-sdk/api/Projects'
import {
  AppBreakpoint,
  IAppBuilder,
  IAppNavBarSettings,
  IDialog,
  IMenu,
  IOverlay,
  IPage,
  IStyle,
  PageType,
} from '@makyoapp/makyo-sdk/models/AppBuilder'
import { IBrandKit } from '@makyoapp/makyo-sdk/models/BrandKits'
import { IProjectPresenter } from '@makyoapp/makyo-sdk/models/Projects'
import { DbItemAdapter } from '@makyoapp/makyo-common'

import { DBClient, IDB } from '@/core/db'
import { IDBProvider, StandardDBProvider, TanstackQueryProvider } from '@/core/db/provider'
import { queryClient, queryKeys } from '@/lib/react-query'

import { brandkitDefaultCustomColor } from '../constants'
import { IAppProvider } from '../provider/provider'
import { TanstackQueryAppProvider } from '../provider/tanstack-query'
import { createCoalescingKey, generateUndoActionId, UndoableOptions } from '../undo'
import { UndoManager } from '../undo/manager'
import { AppOverlay, IAppOverlay } from './overlay'
import { AppPage, IAppPage } from './page'
import { AppClientSubscriber, SubscriberUpdateType } from './types'

const pqueue = new PQueue({ concurrency: 2 })

const mutex = new Mutex()

/**
 * IApp represents an app interface. It holds all the methods required to manipulate the app
 */
export interface IAppClient {
  /**
   * Current Active Database ID opened
   */
  appID?: string
  appUserTableID?: string
  provider: IAppProvider
  projectSettings?: IProjectPresenter
  builderSettings?: IAppBuilder
  dbClient?: IDB
  activeBrandKit?: IBrandKit
  pagesMap: Map<string, IAppPage>
  overlaysMap: Map<string, IAppOverlay>
  databases?: DbItemAdapter[] | null
  /**
   * Undo/Redo manager
   */
  undoManager: UndoManager
  /**
   * @internal appClient instance subscriber callback
   */
  subscribe(callback: AppClientSubscriber<IAppClient>): () => void
  /**
   * @internal appClient instance subscriber callback
   */
  subscribers: Set<AppClientSubscriber<IAppClient>>
  /**
   * Notify subscribers
   */
  notifySubscribers(type: SubscriberUpdateType): void
  /**
   * PullProjectSettings - Fetches the project settings from the app and updates the project settings.
   */
  PullProjectSettings(appId: string): Promise<void>

  /**
   * PullBuilderSettings - Fetches the builder settings from the app and updates the builder settings.
   */
  PullBuilderSettings(appId: string): Promise<void>
  /**
   * InitializeDB - Initializes the dbClient.
   */
  InitializeDB(): Promise<void>
  /**
   * AddPage - Deletes the page from the app.
   */
  AddPage(page: IPage, notifySubscribers?: boolean): Promise<IPage>
  /**
   * ReorderPage - Reorder the page on the app.
   * @param pages - The ordered pages.
   * @returns {Promise<void>} - A promise that resolves when the overlay is deleted.
   */
  ReorderPage(pages: IPage[]): Promise<void>
  /**
   * ReorderPage - Reorder the page on the app.
   * @param pageID - The id of the page to delete .
   * @returns {Promise<void>} - A promise that resolves when the overlay is deleted.
   */
  DeletePage(pageID: string): Promise<void>
  /**
   * UpdateBrandKit - Updates the brandkit in the app.
   * @param brandkit - The brandkit to be updated.
   * @returns {Promise<void>} - A promise that resolves when the brandkit is updated.
   */
  /**
   * AddOverlay - Adds an overlay to the app.
   * @param overlay - The overlay to be added.
   * @returns {Promise<void>} - A promise that resolves when the overlay is added.
   */
  AddOverlay(overlay: IOverlay): Promise<void>
  /**
   * ReorderOverlay - Reorders the overlay in the app.
   * @param overlay - The overlay to be reordered.
   * @returns {Promise<void>} - A promise that resolves when the overlay is reordered.
   */
  ReorderOverlay(overlays: IOverlay[]): Promise<void>
  /**
   * DeleteOverlay - Deletes the overlay from the app.
   * @param overlayID - The ID of the overlay to be deleted.
   * @returns {Promise<void>} - A promise that resolves when the overlay is deleted.
   */
  DeleteOverlay(overlayID: string): Promise<void>
  /**
   * UpdateBrandKit - Updates the brandkit in the app.
   */
  UpdateBrandKit(id: string, body: IUpdateBrandKitBody, options?: UndoableOptions): Promise<void>
  /**
   * UpdateStyle - Updates the style in the app.
   * @param IStyle - The style to be updated.
   * @returns {Promise<IStyle>} - A promise that resolves when the style is updated.
   */
  UpdateBuilderBrandKit(style: IStyle, options?: UndoableOptions): Promise<void>
  /**
   * UpdateLogoBrandkit - Updates the logo in the brandkit.
   * @param brandkitId - The ID of the brandkit.
   * @param data - The data to be updated.
   * @param fileExtension - The file extension.
   */
  UpdateLogoBrandkit(brandkitId: string, data: File, fileExtension: string): Promise<ILogoFileLink>
  /**
   * DeleteLogoBrandkit - Deletes the logo from the brandkit.
   * @param brandkitId - The ID of the brandkit.
   * @param data - The data to be deleted.
   */
  DeleteLogoBrandkit(brandkitId: string, data: ILogoFileLink): Promise<void>
  /**
   * UpdateProjectStatusBarColor - Updates the status bar color in the project.
   * @param projectId - The ID of the project.
   * @param color - The color to be updated.
   * @param colorDark - The dark color to be updated.
   */
  UpdateProjectStatusBarColor(projectId: string, color: string, colorDark: string): Promise<void>
  /**
   * UpdateProjectNavBarColor - Updates the nav bar color in the project.
   * @param projectId - The ID of the project.
   * @param color - The color to be updated.
   * @param colorDark - The dark color to be updated.
   */
  UpdateProjectNavBarColor(projectId: string, color: string, colorDark: string): Promise<void>
  /**
   * UploadAppIcon - Uploads the app icon.
   * @param projectId - The ID of the project.
   * @param emoji - The emoji to be uploaded.
   */
  UploadAppIcon(projectId: string, file: string | File | null): Promise<void>
  /**
   *
   */
  UpdateProjectColorScheme(value: string): Promise<void>
  /**
   * UpdateProjectNavBarVisibility - Updates the nav bar visibility in the project.
   * @param visibility - The visibility to be updated.
   * @param showIcon - The show icon to be updated.
   * @param showTitle - The show title to be updated.
   */
  UpdateProjectNavBarVisibility(visibility: boolean, showIcon?: boolean, showTitle?: boolean): Promise<void>
  /**
   * UpdateProjectSwitchers - Updates the language and theme switcher visibility in the project.
   * @param showLanguageSwitcher - The language switcher visibility to be updated.
   * @param showThemeSwitcher - The theme switcher visibility to be updated.
   */
  UpdateProjectSwitchers(showLanguageSwitcher?: boolean, showThemeSwitcher?: boolean): Promise<void>
  /**
   * Update global header/footer stored at project level
   */
  UpdateGlobals(payload: { _Body: any[]; _BodySettings: Record<string, any> }): Promise<void>
  /**
   * Update per-page visibility override for globals
   */
  UpdatePageGlobalsVisibility(pageId: string, visibility: { header?: boolean; footer?: boolean }): Promise<void>
  /**
   *
   */
  UpdateProjectTextColor(value: string): Promise<void>
  /**
   *
   */
  UpdateNavbarSettings(settings: Record<AppBreakpoint, IAppNavBarSettings>): void
  /**
   *
   */
  UpdateProjectPrivacy(payload: IUpdateProjectPrivacyBody): Promise<void>
  /**
   *
   */
  UpdateGlobalTopbarMenu(menuId: string): Promise<void>
  /**
   * AddDialog - Adds a dialog to the app.
   * @param payload - The payload to be added.
   * @returns {Promise<IDialog>} - A promise that resolves when the dialog is added.
   */
  AddDialog(payload: IDialog): Promise<IDialog>
  /**
   * DeleteDialog - Deletes a dialog from the app.
   */
  DeleteDialog(dialogID: string): Promise<void>
  /**
   * AddMenu - Adds a menu to the app.
   * @param payload - The payload to be added.
   * @returns {Promise<IMenu>} - A promise that resolves when the menu is added.
   */
  AddMenu(payload: IMenu): Promise<IMenu>
  /**
   * DeleteMenu - Deletes a menu from the app.
   * @param menuID - The ID of the menu to be deleted.
   * @returns {Promise<void>} - A promise that resolves when the menu is deleted.
   */
  DeleteMenu(menuID: string): Promise<void>
  /**
   * UpdateMenu - Updates a menu in the app.
   * @param menuID - The ID of the menu to be updated.
   * @param payload - The payload to be updated.
   * @returns {Promise<void>} - A promise that resolves when the menu is updated.
   */
  UpdateMenu(menuID: string, payload: IMenu): Promise<void>
  /**
   * Undo - Undoes the last action
   */
  Undo(): Promise<void>
  /**
   * Redo - Redoes the last undone action
   */
  Redo(): Promise<void>
  /**
   * CanUndo - Check if undo is available
   */
  CanUndo(): boolean
  /**
   * CanRedo - Check if redo is available
   */
  CanRedo(): boolean
  /**
   * GetUndoDescription - Get description of action that will be undone
   */
  GetUndoDescription(): string | undefined
  /**
   * GetRedoDescription - Get description of action that will be redone
   */
  GetRedoDescription(): string | undefined
  /**
   * RevalidateVersionHistory - Revalidate the version history
   */
  RevalidateVersionHistory(): void
  /**
   * Close - Closes the app.
   * @returns {void}
   */
  Close(): void
}

//--------------------------------------------------------------------------------------//
//                              App Client Implementation                               //
//--------------------------------------------------------------------------------------//

const appClientsMap: Map<string, AppClient> = new Map()

export class AppClient implements IAppClient {
  // Identifier
  appID: string
  appUserTableID?: string

  // Core Data
  provider: IAppProvider
  projectSettings?: IProjectPresenter
  builderSettings?: IAppBuilder
  dbClient?: IDB

  // Brandkits
  activeBrandKit?: IBrandKit

  // Editor elements
  pagesMap: Map<string, IAppPage> = new Map()
  overlaysMap: Map<string, IAppOverlay> = new Map()

  // Undo/Redo
  undoManager: UndoManager

  // Subscriber
  subscribers: Set<AppClientSubscriber<IAppClient>> = new Set()

  private constructor(appID: string, provider: IAppProvider = TanstackQueryAppProvider) {
    this.appID = appID
    this.provider = provider
    this.undoManager = new UndoManager(this)
  }

  public subscribe(callback: AppClientSubscriber<IAppClient>): () => void {
    this.subscribers.add(callback)
    return () => {
      this.subscribers.delete(callback)
    }
  }

  notifySubscribers(type: SubscriberUpdateType): void {
    this.subscribers.forEach((callback) => callback(type, this))
  }

  // --------------------------------------------------------------------------------------//
  //                                      Init                                             //
  // --------------------------------------------------------------------------------------//

  public static async Open(appID: string): Promise<AppClient> {
    return await mutex.runExclusive(async () => {
      if (appClientsMap.has(appID)) {
        let existingClient = appClientsMap.get(appID)
        if (existingClient) return existingClient
      }

      const newClient = new AppClient(appID)
      appClientsMap.set(appID, newClient)

      // Check if settings and pagesMap are defined. If yes return void without fetching the data.
      if (newClient.projectSettings && newClient.builderSettings && newClient.pagesMap.size > 0) {
        return newClient
      }

      await newClient.PullProjectSettings(appID)
      await newClient.InitializeDB()
      newClient.setAppUserTableID()
      await newClient.PullBuilderSettings(appID)
      await newClient.PullBrandKitDetail()

      return newClient
    })
  }

  private setAppUserTableID(): void {
    if (!this.projectSettings || !this.dbClient) return

    let userTableId = ''

    // try to get from privacy settings first,
    if (!!this.projectSettings._Privacy._UsersTableID) {
      userTableId = this.projectSettings._Privacy._UsersTableID
      const userTable = this.dbClient.tablesMap.get(userTableId)
      const emailColumn = userTable?.columnsMap.get(this.projectSettings._Privacy._EmailColID)
      if (!!userTable && !!emailColumn) {
        const nameColumn = userTable?.columnsMap.get(this.projectSettings._Privacy._NameColID)
        const photoColumn = userTable?.columnsMap.get(this.projectSettings._Privacy._PictureColID)
        const roleColumn = userTable?.columnsMap.get(this.projectSettings._Privacy._RoleColID)
        const phoneColumn = userTable?.columnsMap.get(this.projectSettings._Privacy._PhoneColID)
        userTable?.SetupUserTableColumns({
          email: emailColumn,
          name: nameColumn,
          photo: photoColumn,
          role: roleColumn,
          phone: phoneColumn,
        })
      }
    }
    // if not found, try to use default user table from db
    else {
      const defaultUserTable = Array.from(this.dbClient.tablesMap.values()).find((table) => table.isUserTable)
      userTableId = defaultUserTable?.id || ''
    }

    this.appUserTableID = userTableId
    window.appUserTableID = this.appUserTableID
  }

  async PullProjectSettings(appId: string): Promise<void> {
    const res = await this.provider.getProject(appId)
    this.projectSettings = res
  }

  async PullBuilderSettings(appId: string): Promise<void> {
    // clear existing pagesMap and overlaysMap
    if (this.pagesMap.size > 0) {
      this.pagesMap.clear()
    }
    if (this.overlaysMap.size > 0) {
      this.overlaysMap.clear()
    }

    const res = await this.provider.getBuilder(appId)
    this.builderSettings = res

    // make sure the globals are set
    this.builderSettings._Globals = {
      _Body: res._Globals._Body || [],
      _BodySettings: res._Globals._BodySettings || {},
    }

    // Create pagesMap
    this.pagesMap = new Map(res._Pages.map((page, index) => [page._id, new AppPage(this, index, page)]))

    // Create overlaysMap
    this.overlaysMap = new Map(
      res._Overlays.map((overlay, index) => [overlay._id, new AppOverlay(this, index, overlay)])
    )
  }

  async InitializeDB(): Promise<void> {
    if (!this.projectSettings) return

    const dbID = this.projectSettings._DataSource._DatabaseID

    this.dbClient = await DBClient.Open(
      dbID,
      (window.appMode === 'preview' ? TanstackQueryProvider : StandardDBProvider) as IDBProvider
    )
  }

  async PullBrandKitDetail(): Promise<void> {
    if (!this.projectSettings) return

    const appBrandkitID = this.builderSettings?._Style._BrandKitID
    if (!appBrandkitID) return

    const brandkit = await this.provider.getBrandkitDetail(appBrandkitID)
    this.activeBrandKit = brandkit

    this.notifySubscribers('brandkit')
  }

  // --------------------------------------------------------------------------------------//
  //                             Builder Globals Methods                                   //
  // --------------------------------------------------------------------------------------//
  async UpdateGlobals(payload: { _Body: any[]; _BodySettings: Record<string, any> }): Promise<void> {
    if (!this.builderSettings) return

    const newBody = payload._Body || []
    const newBodySettings = payload._BodySettings || {}

    const beforeGlobals = structuredClone(this.builderSettings._Globals)

    this.undoManager.execute({
      id: generateUndoActionId(),
      timestamp: Date.now(),
      description: 'Updated globals',
      target: 'builder-globals',
      targetId: this.appID,
      before: beforeGlobals,
      after: null,
      canCoalesce: true,
      coalescingKey: createCoalescingKey('builder-globals', this.appID, 'update'),
    })

    this.builderSettings = {
      ...this.builderSettings,
      _Globals: {
        _Body: newBody,
        _BodySettings: newBodySettings,
      },
    }

    this.notifySubscribers('globals')

    await this.provider.updateBuilderGlobals(this.appID, this.builderSettings._Globals)

    const lastAction = this.undoManager.getHistory().pop()
    if (lastAction) {
      lastAction.after = structuredClone(this.builderSettings._Globals)
    }
  }

  async UpdatePageGlobalsVisibility(pageId: string, visibility: { header?: boolean; footer?: boolean }): Promise<void> {
    const page = this.pagesMap.get(pageId)
    if (!page) return

    const beforeState = structuredClone(page.settings)

    this.undoManager.execute({
      id: generateUndoActionId(),
      timestamp: Date.now(),
      description: 'Updated page globals visibility',
      target: 'page',
      targetId: pageId,
      before: beforeState,
      after: null,
      canCoalesce: true,
      coalescingKey: createCoalescingKey('page', pageId, 'globals-visibility'),
    })

    const current = page.settings._GlobalSettings || {}
    page.settings._GlobalSettings = {
      ...current,
      ...(visibility.header !== undefined && { _HideHeader: !visibility.header }),
      ...(visibility.footer !== undefined && { _HideFooter: !visibility.footer }),
    }

    await page.Update(page.settings, false, { skipUndo: true })

    const lastAction = this.undoManager.getHistory().pop()
    if (lastAction) {
      lastAction.after = structuredClone(page.settings)
    }
  }

  // --------------------------------------------------------------------------------------//
  //                             Pages Methods                                             //
  // --------------------------------------------------------------------------------------//
  async AddPage(page: IPage, notifySubscribers: boolean = true): Promise<IPage> {
    const response = await this.provider.addPage(this.appID, page)
    this.pagesMap.set(response._id, new AppPage(this, this.pagesMap.size, response))

    if (notifySubscribers) {
      this.notifySubscribers('page')

      this.undoManager.execute({
        id: createCoalescingKey('page', response._id),
        timestamp: Date.now(),
        description: `Added page "${response._Settings._Title}"`,
        target: 'page',
        targetId: response._id,
        before: null, // No previous state (new page)
        after: structuredClone(response),
      })
    }

    return response
  }

  async ReorderPage(pages: IPage[]): Promise<void> {
    await this.provider.reorderPage(this.appID, pages)
    this.pagesMap = new Map(pages.map((page, index) => [page._id, new AppPage(this, index, page)]))
    this.notifySubscribers('page')
  }

  async DeletePage(pageID: string, notifySubscribers: boolean = true): Promise<void> {
    // Capture page state before deletion for undo
    const page = this.pagesMap.get(pageID)
    const pageState = page ? structuredClone(page.settings) : null

    await this.provider.deletePage(this.appID, pageID)
    this.pagesMap.delete(pageID)

    if (notifySubscribers) {
      this.notifySubscribers('page')

      // Track in undo manager
      if (pageState) {
        this.undoManager.execute({
          id: createCoalescingKey('page', pageID),
          timestamp: Date.now(),
          description: `Deleted page "${pageState._Settings._Title}"`,
          target: 'page',
          targetId: pageID,
          before: pageState,
          after: null, // No state after deletion
        })
      }
    }
  }

  // --------------------------------------------------------------------------------------//
  //                             Overlays Methods                                          //
  // --------------------------------------------------------------------------------------//

  async AddOverlay(overlay: IOverlay): Promise<void> {
    const response = await this.provider.addOverlay(this.appID, overlay)
    this.overlaysMap.set(response._id, new AppOverlay(this, this.overlaysMap.size, response))
    this.notifySubscribers('overlay')

    this.undoManager.execute({
      id: createCoalescingKey('overlay', overlay._id),
      timestamp: Date.now(),
      description: `Added overlay "${response._Settings._Title}"`,
      target: 'overlay',
      targetId: response._id,
      before: null,
      after: structuredClone(response),
    })
  }

  async DeleteOverlay(overlayID: string): Promise<void> {
    // Capture overlay state before deletion for undo
    const overlay = this.overlaysMap.get(overlayID)
    const overlayState = overlay ? structuredClone(overlay.settings) : null

    await this.provider.deleteOverlay(this.appID, overlayID)
    this.overlaysMap.delete(overlayID)
    this.notifySubscribers('overlay')

    // Track in undo manager
    if (overlayState) {
      this.undoManager.execute({
        id: createCoalescingKey('overlay', overlayID),
        timestamp: Date.now(),
        description: `Deleted overlay "${overlayState._Settings._Title}"`,
        target: 'overlay',
        targetId: overlayID,
        before: overlayState,
        after: null,
      })
    }
  }

  async ReorderOverlay(overlays: IOverlay[]): Promise<void> {
    this.overlaysMap = new Map(overlays.map((overlay, index) => [overlay._id, new AppOverlay(this, index, overlay)]))
    await this.provider.reorderOverlay(this.appID, overlays)
  }

  // --------------------------------------------------------------------------------------//
  //                             Dialogs Methods                                           //
  // --------------------------------------------------------------------------------------//

  async AddDialog(payload: IDialog): Promise<IDialog> {
    const res = await AppBuilderAPI.ReqAddDialog(this.appID, payload)
    this.builderSettings!._Dialogs = [...this.builderSettings!._Dialogs, res]
    this.notifySubscribers('dialog')

    this.undoManager.execute({
      id: createCoalescingKey('dialog', res._id),
      timestamp: Date.now(),
      description: `Added dialog "${res._Name}"`,
      target: 'dialog',
      targetId: res._id,
      before: null,
      after: structuredClone(res),
    })
    return res
  }

  async DeleteDialog(dialogID: string): Promise<void> {
    const dialogState = this.builderSettings?._Dialogs?.find((d) => d._id === dialogID)
    if (!dialogState) return

    await AppBuilderAPI.ReqDeleteDialog(this.appID, dialogID)
    this.builderSettings!._Dialogs = this.builderSettings!._Dialogs.filter((d) => d._id !== dialogID)
    this.notifySubscribers('dialog')

    this.undoManager.execute({
      id: createCoalescingKey('dialog', dialogID),
      timestamp: Date.now(),
      description: `Deleted dialog "${dialogState._Name}"`,
      target: 'dialog',
      targetId: dialogID,
      before: dialogState,
      after: null,
    })
  }

  // --------------------------------------------------------------------------------------//
  //                             Menus Methods                                            //
  // --------------------------------------------------------------------------------------//

  async AddMenu(payload: IMenu): Promise<IMenu> {
    const res = await AppBuilderAPI.ReqAddMenu(this.appID, payload)
    this.builderSettings!._Menus = [...this.builderSettings!._Menus, res]
    this.notifySubscribers('menu')

    this.undoManager.execute({
      id: createCoalescingKey('menu', res._id),
      timestamp: Date.now(),
      description: `Added menu "${res._Title}"`,
      target: 'menu',
      targetId: res._id,
      before: null,
      after: structuredClone(res),
    })
    return res
  }

  async DeleteMenu(menuID: string): Promise<void> {
    const menuState = this.builderSettings?._Menus?.find((m) => m._id === menuID)
    if (!menuState) return

    await AppBuilderAPI.ReqDeleteMenu(this.appID, menuID)
    this.builderSettings!._Menus = this.builderSettings!._Menus.filter((m) => m._id !== menuID)
    this.notifySubscribers('menu')

    this.undoManager.execute({
      id: createCoalescingKey('menu', menuID),
      timestamp: Date.now(),
      description: `Deleted menu "${menuState._Title}"`,
      target: 'menu',
      targetId: menuID,
      before: menuState,
      after: null,
    })
  }

  async UpdateMenu(menuID: string, payload: IMenu): Promise<void> {
    const menuState = this.builderSettings?._Menus?.find((m) => m._id === menuID)
    if (!menuState) return

    // add to undo manager (before state)
    this.undoManager.execute({
      id: generateUndoActionId(),
      timestamp: Date.now(),
      description: `Updated menu "${menuState._Title}"`,
      target: 'menu',
      targetId: menuID,
      before: structuredClone(menuState),
      after: null, // Will be set after update
      canCoalesce: true,
      coalescingKey: createCoalescingKey('menu', menuID, 'update'),
    })

    // update menu
    const res = await AppBuilderAPI.ReqUpdateMenu(this.appID, menuID, payload)
    this.builderSettings!._Menus = this.builderSettings!._Menus.map((m) => (m._id === menuID ? res : m))

    // update the 'after' state in the undo action
    const lastAction = this.undoManager.getHistory().pop()
    if (lastAction) {
      lastAction.after = structuredClone(res)
    }

    this.notifySubscribers('menu')
  }

  // --------------------------------------------------------------------------------------//
  //                             Builder BrandKit                                          //
  // --------------------------------------------------------------------------------------//
  async UpdateBuilderBrandKit(style: IStyle, options?: UndoableOptions): Promise<void> {
    if (!this.builderSettings) return

    // add to undo manager (before state)
    if (!options?.skipUndo) {
      this.undoManager.execute({
        id: generateUndoActionId(),
        timestamp: Date.now(),
        description: `Updated builder brandkit`,
        target: 'builder-brandkit',
        targetId: this.appID,
        before: {
          style: structuredClone(this.builderSettings._Style),
          activeBrandKit: structuredClone(this.activeBrandKit),
        },
        after: null,
      })
    }

    const res = await this.provider.updateBuilderBrandkit(this.appID, style)
    this.builderSettings._Style = res

    await this.PullBrandKitDetail()

    // update the 'after' state in the undo action
    if (!options?.skipUndo) {
      const lastAction = this.undoManager.getHistory().pop()
      if (lastAction) {
        lastAction.after = {
          style: structuredClone(this.builderSettings._Style),
          activeBrandKit: structuredClone(this.activeBrandKit),
        }
      }
    }
  }

  // --------------------------------------------------------------------------------------//
  //                             BrandKits Methods                                         //
  // --------------------------------------------------------------------------------------//
  async UpdateBrandKit(
    brandkitID: string,
    newBrandkitBody: IUpdateBrandKitBody,
    options?: UndoableOptions
  ): Promise<void> {
    if (!this.activeBrandKit || !this.builderSettings) return

    // add to undo manager (before state)
    if (!options?.skipUndo) {
      this.undoManager.execute({
        id: generateUndoActionId(),
        timestamp: Date.now(),
        description: options?.description || `Updated brandkit`,
        target: 'brandkit',
        targetId: brandkitID,
        before: structuredClone(this.activeBrandKit),
        after: null,
      })
    }

    const updatedColors = newBrandkitBody._Colors?.map((color) => ({
      ...color,
      _CustomColors: color._CustomColors.length > 0 ? color._CustomColors : brandkitDefaultCustomColor,
    }))

    const newBrandkit = {
      ...this.activeBrandKit,
      ...(newBrandkitBody._FontsFamily !== undefined && { _FontsFamily: newBrandkitBody._FontsFamily }),
      ...(updatedColors !== undefined && { _Colors: structuredClone(updatedColors) }),
      ...(newBrandkitBody._Name !== undefined && { _Name: newBrandkitBody._Name }),
    }

    this.provider.updateBrandkit(brandkitID, {
      ...newBrandkitBody,
      _Colors: updatedColors,
    })

    this.activeBrandKit = structuredClone(newBrandkit)

    this.notifySubscribers('brandkit')

    // update the 'after' state in the undo action
    if (!options?.skipUndo) {
      const lastAction = this.undoManager.getHistory().pop()
      if (lastAction) {
        lastAction.after = structuredClone(this.activeBrandKit)
      }
    }
  }

  async UpdateLogoBrandkit(brandkitId: string, data: File, fileExtension: string): Promise<ILogoFileLink> {
    if (!this.activeBrandKit) {
      throw new Error('Active brandkit not found')
    }

    const resp = await this.provider.updateLogoBrandkit(brandkitId, data, fileExtension)
    this.activeBrandKit._Logos = [...this.activeBrandKit._Logos, resp._File]
    this.notifySubscribers('brandkit')

    return resp
  }

  async DeleteLogoBrandkit(brandkitId: string, data: ILogoFileLink): Promise<void> {
    if (!this.activeBrandKit) return

    await this.provider.deleteLogoBrandkit(brandkitId, data)
    this.activeBrandKit._Logos = this.activeBrandKit._Logos.filter((l) => l !== data._File)
    this.notifySubscribers('brandkit')
  }

  // --------------------------------------------------------------------------------------//
  //                             Project Methods                                           //
  // --------------------------------------------------------------------------------------//
  async UpdateProjectStatusBarColor(projectId: string, color: string, colorDark: string): Promise<void> {
    if (!this.projectSettings) return

    this.projectSettings._Appearance._StatusBarColor = color
    this.projectSettings._Appearance._StatusBarDarkColor = color

    this.notifySubscribers('project')
    await this.provider.updateProjectStatusBarColor(projectId, color, colorDark)
  }

  async UpdateProjectNavBarColor(projectId: string, color: string, colorDark: string): Promise<void> {
    if (!this.projectSettings) return

    this.projectSettings._Appearance._NavBarColor = color as any
    this.projectSettings._Appearance._NavBarDarkColor = colorDark as any

    this.notifySubscribers('project')

    await this.provider.updateProjectNavBarColor(
      projectId,
      color,
      this.projectSettings._Appearance._NavBarTextColor,
      colorDark
    )
  }

  async UploadAppIcon(projectId: string, file: string | File | null): Promise<void> {
    await this.provider.uploadAppIcon(projectId, file)
  }

  async UpdateProjectColorScheme(value: string) {
    if (!this.projectSettings) return
    this.projectSettings._Appearance._Theme = value as any
    this.notifySubscribers('project')
    await this.provider.updateProjectColorScheme(this.appID, value)
  }

  async UpdateProjectTextColor(textColor: string) {
    if (!this.projectSettings) return

    this.projectSettings._Appearance._NavBarTextColor = textColor as any
    // this.projectSettings._Appearance._TextColor = value as any
    this.notifySubscribers('project')
    await this.provider.updateProjectTextColor(this.appID, this.projectSettings._Appearance._NavBarColor, textColor)
  }

  async UpdateProjectNavBarVisibility(visibility: boolean, showIcon?: boolean, showTitle?: boolean) {
    if (!this.projectSettings) return
    this.projectSettings._Appearance._NavBarVisibility = visibility
    this.notifySubscribers('project')
    await this.provider.updateProjectNavBarVisibility(this.appID, visibility, showIcon, showTitle)
  }

  async UpdateProjectSwitchers(showLanguageSwitcher?: boolean, showThemeSwitcher?: boolean) {
    if (!this.projectSettings) return
    // Store in project settings (will be persisted to backend)
    // NOTE: need polo's help
    // if (showLanguageSwitcher !== undefined) {
    //   this.projectSettings._Appearance._ShowLanguageSwitcher = showLanguageSwitcher
    // }
    // if (showThemeSwitcher !== undefined) {
    //   this.projectSettings._Appearance._ShowThemeSwitcher = showThemeSwitcher
    // }
    this.notifySubscribers('project')
    await this.provider.updateProjectSwitchers(this.appID, showLanguageSwitcher, showThemeSwitcher)
  }

  async UpdateProjectPrivacy(payload: IUpdateProjectPrivacyBody) {
    if (!this.projectSettings) return

    this.projectSettings._Privacy = {
      _PublicAcces: payload._PublicAcces,
      _PostAuthenticatePageID: payload._PostAuthenticatePageID,
      _PostLogoutPageID: payload._PostLogoutPageID,
      _PostUnauthenticatedPageID: payload._PostUnauthenticatedPageID,
      _AuthPlugin: {
        ...this.projectSettings._Privacy._AuthPlugin,
        _PluginID: payload._PluginID,
        _Working: payload._PublicAcces === false,
      },
      _UsersTableID: payload._UsersTableID,
      _EmailColID: payload._EmailColID,
      _NameColID: payload._NameColID,
      _PictureColID: payload._PictureColID,
      _RoleColID: payload._RoleColID,
      _PhoneColID: payload._PhoneColID,
    }

    const res = await this.provider.updateProjectPrivacy(this.appID, payload)

    if (!res._AuthPlugin._Working) {
      // show error message that the puglin is not working anymore
      this.projectSettings._Privacy = {
        ...this.projectSettings._Privacy,
        _AuthPlugin: {
          ...this.projectSettings._Privacy._AuthPlugin,
          _PluginID: '',
          _Working: false,
        },
      }
      this.notifySubscribers('project')
      throw new Error('PLUGIN_NOT_WORKING')
    } else {
      this.projectSettings._Privacy = res
      this.notifySubscribers('project')
    }
  }

  // --------------------------------------------------------------------------------------//
  //                             Navbar Methods                                             //
  // --------------------------------------------------------------------------------------//
  UpdateNavbarSettings(settings: Record<AppBreakpoint, IAppNavBarSettings>) {
    if (!this.builderSettings) return

    this.builderSettings._NavBar = settings
    this.notifySubscribers('navbar')

    this.provider.updateBuilder(this.appID, this.builderSettings)
  }

  // --------------------------------------------------------------------------------------//
  //                             Topbar Methods                                             //
  // --------------------------------------------------------------------------------------//
  async UpdateGlobalTopbarMenu(menuId: string) {
    if (!this.builderSettings) return

    // currently we dont have a place to store global top bar menu in sdk,
    // so we will store it in the pre-defined page (user profile page)
    const userProfilePage = Array.from(this.pagesMap.values()).find(
      (page) => page.settings._Settings._Type === PageType.UserProfile
    )
    if (!userProfilePage) return

    userProfilePage.settings._TopBar._TrailingElement[0] = {
      _Settings: { flag: 64, id: '', value: menuId },
      _Actions: {},
    }
    await userProfilePage.Update(userProfilePage.settings)
  }

  // --------------------------------------------------------------------------------------//
  //                             Undo/Redo Methods                                          //
  // --------------------------------------------------------------------------------------//
  async Undo(): Promise<void> {
    await this.undoManager.undo()
  }

  async Redo(): Promise<void> {
    await this.undoManager.redo()
  }

  CanUndo(): boolean {
    return this.undoManager.canUndo()
  }

  CanRedo(): boolean {
    return this.undoManager.canRedo()
  }

  GetUndoDescription(): string | undefined {
    return this.undoManager.getUndoDescription()
  }

  GetRedoDescription(): string | undefined {
    return this.undoManager.getRedoDescription()
  }

  /**
   * batch revalidate version history within 5 seconds interval
   */
  RevalidateVersionHistory = debounce(
    async () => {
      await queryClient.invalidateQueries({ queryKey: [queryKeys.appVersionHistory, this.appID] })
    },
    1000 * 5, // 5 seconds
    {
      leading: false,
      trailing: true,
    }
  )

  Close(): void {
    // Clear undo/redo history
    this.undoManager.clear()

    // Clear app data
    appClientsMap.delete(this.appID)
    this.pagesMap.clear()
    this.overlaysMap.clear()
    this.dbClient?.Close()
    this.dbClient = undefined
    this.projectSettings = undefined
    this.builderSettings = undefined
    this.activeBrandKit = undefined
    this.subscribers.clear()
    pqueue.clear()
    this.appUserTableID = ''
    window.appUserTableID = ''
  }
}
